/**
 * AnalyticsChipToolbarFilter — the toolbar control shape shared by the Analytics page's
 * TWO status facets: Certificate status and Scan status (VERA-260).
 *
 * ## Why this exists at all — §36, on the second hand-roll
 *
 * `AnalyticsStatusToolbarFilter` was written for one facet. Promoting scan status from a
 * card-local control to a real page-level filter made it the SECOND control with exactly
 * this anatomy: a `Button` trigger reading `Label: [chip] [chip]`, opening a `Popover`
 * whose body is the drawer's own `StatusChipFilter`. Copying it would have produced two
 * near-identical files that then drift on the details that make the shape work — the
 * portal-vs-nested-button rule, the `StatusChip`-not-`StatusPill` rule, the rest-state
 * label, the icon sizing taken from the date field's constants. Those are exactly the
 * details a second copy loses first, so there is one component and two thin callers.
 *
 * The invariants below are the reason this is a component rather than a snippet. Each was
 * arrived at once and now holds for both facets by construction.
 *
 * ## One state, two access paths
 *
 * This owns NO selection state. It reads and writes whichever `state.analytics.filters`
 * key its caller hands it — the SAME key the drawer's own `StatusChipFilter` field for
 * that facet writes. A change made here is instantly reflected in the drawer chip row and
 * vice versa, and the applied-filter chip clears identically from either surface.
 *
 * ## Why the popover body is the real `StatusChipFilter`, not a lookalike
 *
 * Per §28/§36/§47: the drawer's own note says "the chips ARE the design" for these facets,
 * so the popover embeds `StatusChipFilter` itself — same options, same `StatusPill` toggle
 * chips, same Clear affordance — rather than a second hand-rolled option list that could
 * drift from the drawer's.
 *
 * `Popover` PORTALS its children to `document.body`, so `StatusChipFilter`'s `StatusPill`
 * buttons are NOT DOM descendants of this component's own trigger `<button>` — nesting a
 * real `<button>` inside another `<button>` is invalid HTML and breaks focus/click
 * semantics, and the portal is what keeps that from happening here.
 *
 * ## Why the TRIGGER'S inline chips are `StatusChip`, not `StatusPill`
 *
 * The trigger itself IS a `<button>` (`@repo/ui` `Button`), and unlike the popover body,
 * anything rendered as its own `children` DOES land in the DOM as a descendant of that
 * button. `StatusPill` renders a real `<button>` (see that component's own docblock on why
 * it moved off a bare `div`), so using it here would nest an interactive button inside
 * another — the same defect the portal avoids above. `StatusChip` (the read-only status
 * token used on every table's Status column) renders a plain MUI `Chip`
 * (non-interactive), which is exactly what a chip DISPLAYED inside another control's
 * trigger needs to be. It reads the same statuses map the options are built from, so the
 * colours are the same source as everywhere else that status is drawn (§28/§36).
 *
 * ## Collapsed vs populated width — a user directive, not a summary count
 *
 * Collapsed (nothing selected) the trigger costs one control's width — just the label.
 * With a selection it reads LABEL then VALUES — `Certificate status: [Created] [Active]`
 * — never bare pills with nothing saying what they are, and never a "2 selected" text
 * summary either: the values render as the real status chips, inline, so the toolbar
 * states both the filter AND its current value without opening anything. The trigger is
 * content-sized (grows with the selection, same as the date range control beside it)
 * rather than a fixed box with its own internal truncation — the responsive DROP in
 * `AnalyticsFiltersHeader` (not an overflow ellipsis here) is what keeps a wide selection
 * from crowding the toolbar; see that file's staged `TOOLBAR_*_DROP_SX` constants for why
 * drop, not wrap, is the mechanism.
 *
 * ## The label must NAME ITS AXIS — "Certificate status", never a bare "Status"
 *
 * This page counts TWO things and now filters on TWO status vocabularies. A certificate is
 * Created / Active / Lost / Stolen; a scan is Verified / Invalid / Opened. A bare "Status"
 * names neither, and with both controls in the same row it would be actively ambiguous.
 * The drawer field and the applied-filter chip carry the same nouns, per §44 (one label
 * per field, never two). Sentence case here is weight, not naming — the noun is what has
 * to match.
 */
import React, { useRef, useState } from 'react'

import { Box, Popover, Stack, Typography } from '@mui/material'

import { ChevronDownIcon } from 'lucide-react'

import { Button } from '@repo/ui'

// Direct file imports, not the `components` barrel — mirrors `AnalyticsSearchTypeahead`'s
// own caution beside this file: that barrel re-exports both `input` (StatusChipFilter) and
// `display` (StatusChip), and this module does not need the whole surface either symbol's
// barrel drags in.
import { DATE_FIELD_GAP, DATE_FIELD_ICON_SIZE } from 'components/input/filters/dateRangeChrome'
import { StatusChipFilter } from 'components/input/filters/StatusChipFilter'
import { filterDropdownPaperSx } from 'components/input/filters/dropdownSurface'
import { StatusChip } from 'components/display/StatusChip/StatusChip'

export interface AnalyticsChipToolbarFilterProps {
  /**
   * Sentence-case, and it must name its axis — see the docblock. Rendered with a trailing
   * colon once there is a selection to introduce, bare when there is not.
   */
  label: string
  /**
   * LEADING GLYPH, matching the date control and the search field beside it. The toolbar's
   * other fields each open with an icon; without one this control reads as a bare word in
   * a row of iconed fields (user directive).
   *
   * A component type rather than an element, so this file sets the size and stroke from
   * the DATE field's own constants — the two controls then line up by construction rather
   * than by coincidence, and a retune of the date field's inset carries here.
   */
  icon: React.ElementType
  /** The `StatusChipFilter` options — `{ value, label, color, backgroundColor, borderColor }`. */
  options: Array<{
    value: string
    label: string
    color: string
    backgroundColor?: string
    borderColor?: string
  }>
  /** The `{ [value]: Status }` map `StatusChip` paints the trigger's inline chips from. */
  statuses: Record<
    string,
    { label: string; color: string; borderColor?: string; backgroundColor?: string; glossaryId?: string }
  >
  /** Title-case, matching this facet's drawer field — `StatusChipFilter` renders it. */
  popoverTitle: string
  /** The redux key this facet writes, carried through for parity with the drawer column. */
  field: string
  /** The current selection, in whatever form the caller's drawer field also reads. */
  value: string[]
  /** Fires with the next selection, in that same form. */
  onChange: (next: string[]) => void
}

export const AnalyticsChipToolbarFilter = ({
  label,
  icon: Icon,
  options,
  statuses,
  popoverTitle,
  field,
  value,
  onChange,
}: AnalyticsChipToolbarFilterProps) => {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement | null>(null)

  const hasSelection = value.length > 0

  return (
    <>
      {/* `size="large"` = 49px (`TOOLBAR_CONTROL_HEIGHT`), matching every other control in
          this band — the same size the toolbar's own Download button uses (§29). Height is
          set via the variant's built-in `large` size rather than a bespoke `sx` height, so
          this stays immune to the `Button` `sx`-merge trap noted elsewhere in this epic
          (a FUNCTION `sx` on `Button` silently applies nothing — a plain size prop has no
          such hazard). */}
      <Button
        ref={anchorRef}
        size="large"
        variant="secondary"
        disableElevation
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <Stack direction="row" spacing={0} alignItems="center" sx={{ gap: `${DATE_FIELD_GAP}px` }}>
          <Icon size={DATE_FIELD_ICON_SIZE} strokeWidth={1} />
          {/* LABEL, THEN VALUES — never bare pills (user directive). A row of coloured
              words with nothing saying what they ARE does not identify itself in a toolbar
              sitting beside a date range, a search box and a SECOND status control. Shown
              at REST too (no colon, once there is nothing to introduce) so the control
              identifies itself even unused. */}
          <Typography
            variant="body2"
            sx={{ whiteSpace: 'nowrap', color: hasSelection ? 'text.primary' : 'text.secondary' }}
          >
            {hasSelection ? `${label}:` : label}
          </Typography>
          {hasSelection && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
              {value.map(v => (
                <StatusChip key={v} statuses={statuses} status={v} size="small" />
              ))}
            </Stack>
          )}
          <ChevronDownIcon
            size={18}
            strokeWidth={1}
            style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}
          />
        </Stack>
      </Button>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: { sx: theme => filterDropdownPaperSx(theme, 300) },
        }}
      >
        {/*
          🚨 `width: '100%'`, NEVER a repeat of the paper's own number. This read
          `width: 300` — the same 300 handed to `filterDropdownPaperSx` above — which
          double-counted the paper's gutter and CLIPPED the field's Clear action.

          The arithmetic, measured in the browser at a 1920 viewport with the Scan Status
          popover open: paper `1153 → 1453` (300 wide, `p: '8px'` + a 1px border), this Box
          `1162 → 1462`. It overflowed the paper's border box by 9px, and the paper sets
          `overflowX: 'hidden'`, so those 9px were cut. `FilterField`'s label row is
          `justifyContent: space-between`, which pins Clear to the row's right edge — so the
          clipped 9px landed squarely on it: Clear measured `1420.75 → 1454`, i.e. its last
          pixel sat outside the panel and it rendered against the border with no gutter at
          all. Certificate Status had it identically; one component, one fix.

          `100%` resolves against the paper's CONTENT box (284px here), so the gutter is
          stated once — by the paper — and this Box's own `p: 2` is a second, intentional
          inset on top of it rather than an accidental overhang.
        */}
        <Box sx={{ p: 2, width: '100%' }}>
          {/* The REAL drawer field — same options, same toggle chips, same Clear. See the
              module docblock for why this is embedded rather than rebuilt. `field` is
              carried through for parity with the drawer's own column shape even though
              this popover ignores it (only `.value` is read below). */}
          <StatusChipFilter
            column={{ headerName: popoverTitle, options, field }}
            filter={value}
            onChange={f => onChange(f.value)}
          />
        </Box>
      </Popover>
    </>
  )
}

export default AnalyticsChipToolbarFilter
